import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import AbTestsList from './AbTestsList';
import styled from 'styled-components';
import CreateAbTestModal, { SubmitButton } from './CreateAbTestModal';

const TESTS_PER_PAGE = 8;

const ContentWrapper = styled.div`
  width: 1360px;
  max-width: 100%;
  margin: 24px auto 0;
`;

const AbTestsApp = ({ domains: initialDomainsData, landings: initialLandingData }) => {
  const [domains] = useState(initialDomainsData);
  const [landings] = useState(initialLandingData);
  const [selectedLandingNames, setSelectedLandingNames] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(true);
  const [deletingTestId, setDeletingTestId] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState({ value: 'All', label: 'All Countries' });
  const [testName, setTestName] = useState('');
  const [comment, setComment] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState(() => [
    {
      startDate: new Date(new Date().setHours(0, 0, 0, 0)),
      endDate: new Date(new Date().setHours(23, 59, 59, 999)),
      key: 'selection',
    },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: TESTS_PER_PAGE,
    total_pages: 1,
    total_count: 0,
  });
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const dateRangePickerRef = useRef();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewStatistics, setPreviewStatistics] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const previewRequestRef = useRef(false);
  const createRequestRef = useRef(false);
  const [isPreviewDirty, setIsPreviewDirty] = useState(true);
  const [landingImages, setLandingImages] = useState(() => new Map());

  const handleOutsideClick = useCallback((event) => {
    if (dateRangePickerRef.current && !dateRangePickerRef.current.contains(event.target)) {
      setIsDateRangePickerOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const domainMap = useMemo(() => {
    const map = {};
    domains.forEach((domain) => {
      map[domain.id] = domain.name;
    });
    return map;
  }, [domains]);

  const landingOptions = useMemo(
    () =>
      landings
        .map((landing) => ({
          id: landing.id,
          displayName: `${domainMap[landing.domain_id] ? `${domainMap[landing.domain_id]}/` : ''}${landing.name}`,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [landings, domainMap]
  );

  const landingNameToId = useMemo(() => {
    const map = new Map();
    landingOptions.forEach((option) => {
      map.set(option.displayName.toLowerCase(), option.id);
    });
    return map;
  }, [landingOptions]);

  const orderPreviewStatistics = useCallback(
    (stats) => {
      if (!Array.isArray(stats)) {
        return [];
      }

      const orderMap = new Map(
        selectedLandingNames
          .map((name, index) => [name?.toString().trim().toLowerCase(), index])
      );

      return [...stats]
        .map((item, index) => {
          const key = (item?.url || item?.name || '').toString().trim().toLowerCase();
          const orderIndex = orderMap.has(key) ? orderMap.get(key) : selectedLandingNames.length + index;
          return { orderIndex, item };
        })
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map(({ item }) => item);
    },
    [selectedLandingNames]
  );

  const aggregateStatisticsByLanding = useCallback((stats) => {
    if (!Array.isArray(stats)) {
      return [];
    }

    const landingMap = new Map();

    stats.forEach((item) => {
      if (!item) {
        return;
      }

      const landingKey = item.landing_id || item.url;
      if (!landingKey) {
        return;
      }

      const existing = landingMap.get(landingKey);
      const currentPageLoad = Number(item.page_load || 0);
      const existingPageLoad = Number(existing?.page_load || 0);

      if (!existing || currentPageLoad > existingPageLoad) {
        landingMap.set(landingKey, { ...item });
      }
    });

    return Array.from(landingMap.values()).map((entry) => {
      const trafficSource = entry.traffic_source || '-';
      return {
        ...entry,
        traffic_source: trafficSource,
        user_visits: Number(entry.user_visits || 0),
        registrations: Number(entry.registrations || 0),
        form_errors: Number(entry.form_errors || 0),
        sms_successes: Number(entry.sms_successes || 0),
        sms_errors: Number(entry.sms_errors || 0),
        page_load: Number(entry.page_load || 0),
        app_download: Number(entry.app_download || 0),
      };
    });
  }, []);

  const resetCreationState = useCallback(() => {
    setTestName('');
    setComment('');
    setSelectedLandingNames([]);
    setSelectedCountry({ value: 'All', label: 'All Countries' });
    setSelectedDateRange([
      {
        startDate: new Date(new Date().setHours(0, 0, 0, 0)),
        endDate: new Date(new Date().setHours(23, 59, 59, 999)),
        key: 'selection',
      },
    ]);
    setPreviewStatistics([]);
    setLandingImages(new Map());
    setIsPreviewDirty(true);
    setIsDateRangePickerOpen(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    resetCreationState();
    setIsModalOpen(true);
  }, [resetCreationState]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    resetCreationState();
  }, [resetCreationState]);

  const getSelectedLandingIds = useCallback(() => {
    if (selectedLandingNames.length === 0) {
      toast.error('Select at least one landing');
      return null;
    }

    const normalizedSelections = selectedLandingNames
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    const landingIds = [];
    const missingLandings = [];

    normalizedSelections.forEach((name) => {
      const lookupKey = name.toLowerCase();
      const landingId = landingNameToId.get(lookupKey);
      if (landingId) {
        landingIds.push(landingId);
      } else {
        missingLandings.push(name);
      }
    });

    if (landingIds.length === 0) {
      toast.error('No landings found for the provided names');
      return null;
    }

    if (missingLandings.length > 0) {
      toast.error(`Missing landings: ${missingLandings.join(', ')}`);
    }

    const uniqueLandingIds = Array.from(new Set(landingIds));

    if (uniqueLandingIds.length < 2) {
      toast.error('A/B test must include at least two landings');
      return null;
    }

    return uniqueLandingIds;
  }, [selectedLandingNames, landingNameToId]);

  const requestStatistics = useCallback(
    async (landingIds) => {
      const formattedDateRange = [
        {
          startDate: selectedDateRange[0].startDate.toISOString(),
          endDate: selectedDateRange[0].endDate.toISOString(),
        },
      ];

      const tokenElement = document.querySelector('meta[name="csrf-token"]');
      if (!tokenElement) {
        throw new Error('Missing CSRF token');
      }

      const payload = {
        selectedDomain: 'all',
        selectedLandings: landingIds,
        dateRange: formattedDateRange,
        selectedCountry: selectedCountry.value,
      };


      const response = await fetch('/api/requests/ab_tests', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': tokenElement.content,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load landing statistics');
      }

      const data = await response.json();
      const aggregatedStatistics = aggregateStatisticsByLanding(data.statistics);

      if (aggregatedStatistics.length === 0) {
        toast.error('No statistics gathered for selected landings');
        return null;
      }

      if (aggregatedStatistics.length < 2) {
        toast.error('A/B test must include at least two landings');
        return null;
      }

      return aggregatedStatistics;
    },
    [aggregateStatisticsByLanding, selectedCountry.value, selectedDateRange]
  );

  const fetchAbTests = useCallback(async (page = 1) => {
    setTestsLoading(true);
    try {
      const response = await fetch(`/api/ab_tests.json?page=${page}&per_page=${TESTS_PER_PAGE}`, {
        headers: {
          Accept: 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load A/B tests');
      }
      const data = await response.json().catch(() => ({ ab_tests: [] }));
      setAbTests(Array.isArray(data.ab_tests) ? data.ab_tests : []);
      if (data.pagination) {
        const totalPages = data.pagination.total_pages || 1;
        const safePage = Math.max(1, Math.min(data.pagination.page || page, totalPages));
        setPagination({
          page: safePage,
          per_page: data.pagination.per_page || TESTS_PER_PAGE,
          total_pages: totalPages,
          total_count: data.pagination.total_count || 0,
        });
        setCurrentPage(totalPages === 0 ? 1 : safePage);
      } else {
        setPagination({
          page,
          per_page: TESTS_PER_PAGE,
          total_pages: 1,
          total_count: Array.isArray(data.ab_tests) ? data.ab_tests.length : 0,
        });
        setCurrentPage(page);
      }
      return data;
    } catch (error) {
      console.error('Error fetching A/B tests:', error);
      toast.error(error.message || 'Failed to load A/B tests');
      return null;
    } finally {
      setTestsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAbTests(1);
  }, [fetchAbTests]);

  const handlePageChange = useCallback((page) => {
    if (page < 1 || page > pagination.total_pages || page === currentPage) {
      return;
    }
    fetchAbTests(page);
  }, [fetchAbTests, pagination.total_pages, currentPage]);

  const handleDeleteTest = useCallback(async (test) => {
    if (!test || deletingTestId) {
      return;
    }

    const confirmed = window.confirm(`Delete A/B test "${test.name}"?`);
    if (!confirmed) {
      return;
    }

    const token = document.querySelector('meta[name="csrf-token"]').content;
    setDeletingTestId(test.id);

    try {
      const response = await fetch(`/api/ab_tests/${test.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': token,
          Accept: 'application/json',
        },
      });

      if (response.status !== 204 && !response.ok) {
        throw new Error('Failed to delete A/B test');
      }

      toast.success('A/B test deleted');
      const data = await fetchAbTests(currentPage);
      let totalPages = pagination.total_pages || 1;

      if (data && data.pagination && typeof data.pagination.total_pages === 'number') {
        totalPages = data.pagination.total_pages;
      }

      if (totalPages === 0) {
        setAbTests([]);
        setPagination({ page: 1, per_page: TESTS_PER_PAGE, total_pages: 1, total_count: 0 });
        setCurrentPage(1);
        return;
      }

      if (currentPage > totalPages) {
        await fetchAbTests(totalPages);
      }
    } catch (error) {
      console.error('Error deleting A/B test:', error);
      toast.error(error.message || 'Failed to delete A/B test');
    } finally {
      setDeletingTestId(null);
    }
  }, [deletingTestId, fetchAbTests, currentPage, pagination.total_pages]);

  const handleGetInfo = useCallback(async () => {
    if (previewRequestRef.current) {
      return;
    }

    const landingIds = getSelectedLandingIds();
    if (!landingIds) {
      return;
    }

    previewRequestRef.current = true;
    setIsPreviewLoading(true);
    setIsDateRangePickerOpen(false);

    try {
      const aggregatedStatistics = await requestStatistics(landingIds);
      if (!aggregatedStatistics) {
        return;
      }

      const orderedStats = orderPreviewStatistics(aggregatedStatistics);
      setPreviewStatistics(orderedStats);
      setIsPreviewDirty(false);
      
      setLandingImages((prev) => {
        const newMap = new Map(prev);
        const currentLandingKeys = new Set(
          orderedStats.map((stat) => {
            const key = stat.landing_id || stat.url || stat.name || '';
            return key.toString();
          })
        );
        for (const key of newMap.keys()) {
          if (!currentLandingKeys.has(key.toString())) {
            newMap.delete(key);
          }
        }
        return newMap;
      });
    } catch (error) {
      console.error('Error fetching preview statistics:', error);
      toast.error(error.message || 'Failed to load statistics preview');
    } finally {
      setIsPreviewLoading(false);
      previewRequestRef.current = false;
    }
  }, [getSelectedLandingIds, requestStatistics, orderPreviewStatistics]);

  const handleCreateTest = useCallback(async () => {
    if (createRequestRef.current) {
      return;
    }

    if (!testName.trim()) {
      toast.error('Enter A/B test name');
      return;
    }

    const landingIds = getSelectedLandingIds();
    if (!landingIds) {
      return;
    }

    createRequestRef.current = true;
    setIsCreating(true);
    setIsDateRangePickerOpen(false);

    try {
      let statisticsForCreation = previewStatistics;

      if (isPreviewDirty || !Array.isArray(statisticsForCreation) || statisticsForCreation.length < 2) {
        const freshStatistics = await requestStatistics(landingIds);
        if (!freshStatistics) {
          return;
        }
        statisticsForCreation = orderPreviewStatistics(freshStatistics);
        setPreviewStatistics(statisticsForCreation);
        setIsPreviewDirty(false);
      }

      if (!statisticsForCreation || statisticsForCreation.length < 2) {
        toast.error('A/B test must include at least two landings');
        return;
      }

      const tokenElement = document.querySelector('meta[name="csrf-token"]');
      if (!tokenElement) {
        throw new Error('Missing CSRF token');
      }

      const landingsPayload = statisticsForCreation.map((stat) => ({
        name: stat.url,
        pageloads: Math.round(stat.page_load || 0),
        registrations: Math.round(stat.registrations || 0),
      }));

      const formData = new FormData();
      
      formData.append('ab_test[name]', testName.trim());
      formData.append('ab_test[geo]', selectedCountry.value === 'All' ? 'all' : selectedCountry.value);
      formData.append('ab_test[date]', selectedDateRange[0].startDate.toISOString().split('T')[0]);
      formData.append('ab_test[start_date]', selectedDateRange[0].startDate.toISOString().split('T')[0]);
      formData.append('ab_test[end_date]', selectedDateRange[0].endDate.toISOString().split('T')[0]);
      
      if (comment && comment.trim()) {
        formData.append('ab_test[comment]', comment.trim());
      }

      landingsPayload.forEach((landing, index) => {
        const stat = statisticsForCreation[index];
        const landingKey = stat.landing_id || stat.url || stat.name || '';
        const imageFile = landingImages.get(landingKey.toString());
        
        formData.append(`ab_test[landings][${index}][name]`, landing.name);
        formData.append(`ab_test[landings][${index}][pageloads]`, landing.pageloads);
        formData.append(`ab_test[landings][${index}][registrations]`, landing.registrations);
        
        if (imageFile) {
          formData.append(`ab_test[landing_images][${index}]`, imageFile);
        }
      });


      const createResponse = await fetch('/api/ab_tests', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': tokenElement.content,
        },
        body: formData,
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}));
        throw new Error(errorData.errors ? errorData.errors.join(', ') : 'Failed to create A/B test');
      }

      toast.success('A/B test saved');
      setCurrentPage(1);
      fetchAbTests(1);
      handleCloseModal();
    } catch (error) {
      console.error('Error creating A/B test:', error);
      toast.error(error.message || 'Failed to create A/B test');
    } finally {
      setIsCreating(false);
      createRequestRef.current = false;
    }
  }, [
    fetchAbTests,
    getSelectedLandingIds,
    handleCloseModal,
    isPreviewDirty,
    previewStatistics,
    requestStatistics,
    selectedCountry.value,
    selectedDateRange,
    testName,
    comment,
    orderPreviewStatistics,
  ]);

  const handleDateRangeChange = (ranges) => {
    const currentStartDate = selectedDateRange[0].startDate;
    const currentEndDate = selectedDateRange[0].endDate;

    const startHours = currentStartDate.getHours();
    const startMinutes = currentStartDate.getMinutes();
    const endHours = currentEndDate.getHours();
    const endMinutes = currentEndDate.getMinutes();

    const newStartDate = new Date(ranges.selection.startDate);
    newStartDate.setHours(startHours, startMinutes);

    const newEndDate = new Date(ranges.selection.endDate);
    newEndDate.setHours(endHours, endMinutes);

    setSelectedDateRange([
      {
        startDate: newStartDate,
        endDate: newEndDate,
        key: 'selection',
      },
    ]);
    setIsPreviewDirty(true);
  };

  const handleDateRangeFieldClick = () => {
    setIsDateRangePickerOpen(!isDateRangePickerOpen);
  };

  const handleTimeChange = (newDateRange) => {
    setSelectedDateRange(newDateRange);
    setIsPreviewDirty(true);
  };

  const handleLandingImageChange = useCallback((landingKey, file) => {
    const key = landingKey?.toString() || '';
    if (!file) {
      setLandingImages((prev) => {
        const newMap = new Map(prev);
        newMap.delete(key);
        return newMap;
      });
      return;
    }

    setLandingImages((prev) => {
      const newMap = new Map(prev);
      newMap.set(key, file);
      return newMap;
    });
  }, []);

  return (
    <div className="wrapper">
      <div className="block sticky-top">
        <SubmitButton type="button" onClick={handleOpenModal}>
          Create
        </SubmitButton>
      </div>

      <ContentWrapper>
        <div className="block block-column">
          {testsLoading ? (
            <div>Loading A/B tests...</div>
          ) : (
            <AbTestsList
              tests={abTests}
              pagination={pagination}
              onPageChange={handlePageChange}
              onDelete={handleDeleteTest}
              deletingId={deletingTestId}
            />
          )}
        </div>
      </ContentWrapper>

      <CreateAbTestModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        testName={testName}
        onTestNameChange={setTestName}
        comment={comment}
        onCommentChange={setComment}
        selectedCountry={selectedCountry}
        onCountryChange={(value) => {
          setSelectedCountry(value);
          setIsPreviewDirty(true);
        }}
        selectedDateRange={selectedDateRange}
        onDateRangeFieldClick={handleDateRangeFieldClick}
        isDateRangePickerOpen={isDateRangePickerOpen}
        onDateRangeChange={handleDateRangeChange}
        onTimeChange={handleTimeChange}
        dateRangePickerRef={dateRangePickerRef}
        landingOptions={landingOptions}
        selectedLandingNames={selectedLandingNames}
        onLandingNamesChange={(value) => {
          setSelectedLandingNames(value);
          setIsPreviewDirty(true);
        }}
        isPreviewDirty={isPreviewDirty}
        previewStatistics={previewStatistics}
        isPreviewLoading={isPreviewLoading}
        onGetInfo={handleGetInfo}
        isCreating={isCreating}
        onCreate={handleCreateTest}
        landingImages={landingImages}
        onLandingImageChange={handleLandingImageChange}
      />

      <Toaster position="top-right" />
    </div>
  );
};

export default AbTestsApp;
